import React, { useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  SmartToy as BotIcon,
} from '@mui/icons-material';
import { useChatConversation } from '../../hooks/useChatConversation';
import { useHelpContext } from '../../contexts/HelpWidgetContext';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';

export const ChatContainer: React.FC = () => {
  const { currentPage } = useHelpContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    isLoading,
    error,
    rateLimit,
    send,
  } = useChatConversation({ currentPage });

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = (message: string) => {
    send(message);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 180px)',
        minHeight: 400,
      }}
    >
      {/* Rate Limit Info */}
      {rateLimit && (
        <Box mb={2}>
          <Chip
            size="small"
            label={`${rateLimit.remaining}/${rateLimit.limit} messages remaining today`}
            color={rateLimit.remaining < 3 ? 'warning' : 'default'}
            variant="outlined"
          />
        </Box>
      )}

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          mb: 2,
        }}
      >
        {messages.length === 0 ? (
          <Box textAlign="center" py={4}>
            <BotIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Hi! I'm your Elevair assistant
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              I can help you with anything about the app. Try asking:
            </Typography>
            <Stack spacing={1} alignItems="center">
              {[
                'How do I create a recurring task?',
                'What keyboard shortcuts are available?',
                'How do I link tasks to projects?',
              ].map((suggestion) => (
                <Paper
                  key={suggestion}
                  onClick={() => handleSend(suggestion)}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography variant="body2">{suggestion}</Typography>
                </Paper>
              ))}
            </Stack>
          </Box>
        ) : (
          <Stack spacing={2}>
            {messages.map((message, index) => (
              <ChatMessage key={index} message={message} />
            ))}

            {isLoading && (
              <Box display="flex" alignItems="center" gap={1} p={2}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Thinking...
                </Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Stack>
        )}
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Input */}
      <ChatInput
        onSend={handleSend}
        disabled={isLoading || (rateLimit?.remaining === 0)}
        placeholder={
          rateLimit?.remaining === 0
            ? 'Daily limit reached. Upgrade for more.'
            : 'Ask a question...'
        }
      />
    </Box>
  );
};

export default ChatContainer;
